import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Grid,
  Chip,
  Stack,
  Button,
} from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import Launch from '@mui/icons-material/Launch';
import { Link as RouterLink } from 'react-router-dom';
import ImageCarousel from '../components/ImageCarousel';
import SEO from '../components/SEO';

// Import images
import presentationPage from '../assets/haikei/presentationPage.png';
import gallery from '../assets/haikei/gallery.png';
import photoDetailPage from '../assets/haikei/photoDetailPage.png';
import login from '../assets/haikei/login.png';

function HaikeiArt() {
  const { t } = useTranslation();
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  const sections = [
    {
      title: t('haikei_page.sections.presentation.title'),
      content: t('haikei_page.sections.presentation.content')
    },
    {
      title: t('haikei_page.sections.objectives.title'),
      items: t('haikei_page.sections.objectives.items', { returnObjects: true })
    },
    {
      title: t('haikei_page.sections.architecture.title'),
      tech: t('haikei_page.sections.architecture.tech', { returnObjects: true })
    },
    {
      title: t('haikei_page.sections.features.title'),
      features: t('haikei_page.sections.features.items', { returnObjects: true })
    },
  ];

  const screenshots = [
    { src: presentationPage, title: t('haikei_page.screenshots.presentation') },
    { src: gallery, title: t('haikei_page.screenshots.gallery') },
    { src: photoDetailPage, title: t('haikei_page.screenshots.detail') },
    { src: login, title: t('haikei_page.screenshots.login') },
  ];

  return (
    <>
      <SEO 
        title={`Haikei.art - ${t('hero.name')}`}
        description={t('haikei_page.header.description')}
        type="article"
      />
      <Box sx={{ pb: 10, position: 'relative', overflowX: 'hidden', width: '100%' }}>
        {/* Header Background */}
        <Box
        sx={{
          pt: 12,
          pb: 8,
          width: '100%',
          backgroundColor: '#0e1626',
          backgroundImage: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(14, 22, 38, 0.9) 100%)',
          display: 'flex',
          alignItems: 'center',
          borderBottom: '1px solid #1f2a3d',
          overflowX: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Stack direction="row" spacing={2} sx={{ mb: 4 }}>
            <Button
              component={RouterLink}
              to="/"
              startIcon={<ArrowBack />}
              sx={{ color: '#fff' }}
            >
              {t('haikei_page.back')}
            </Button>
            <Button
              href="https://haikei.art/"
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<Launch />}
              variant="contained"
              sx={{ 
                bgcolor: '#ef4444', 
                '&:hover': { bgcolor: '#dc2626' },
                color: '#fff',
                px: 3
              }}
            >
              {t('haikei_page.visitSite')}
            </Button>
          </Stack>
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2.5rem', md: '4.5rem' },
              color: '#fff',
              mb: 2,
              fontWeight: 900,
              fontFamily: '"Excellence In Motion", sans-serif',
              textShadow: '0 4px 30px rgba(0,0,0,0.5)',
            }}
          >
            {t('haikei_page.title')}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
            <Chip label="React" sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: '#fff', borderRadius: 0 }} />
            <Chip label="Node.js" sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: '#fff', borderRadius: 0 }} />
            <Chip label="PostgreSQL" sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: '#fff', borderRadius: 0 }} />
            <Chip label="Mollie" sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: '#fff', borderRadius: 0 }} />
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: -5, position: 'relative', zIndex: 2 }}>
        <Grid container spacing={4}>
          {/* Main Content */}
          <Grid item xs={12} md={8}>
            <Box sx={{ bgcolor: '#131d2f', p: { xs: 3, md: 6 }, border: '1px solid #1f2a3d' }}>
              {sections.map((section, idx) => (
                <Box key={idx} sx={{ mb: 6 }}>
                  <Typography variant="h4" sx={{ color: '#fff', mb: 3, fontSize: '1.5rem', fontWeight: 600 }}>
                    {section.title}
                  </Typography>
                  
                  {section.content && (
                    <Typography variant="body1" sx={{ color: '#b8c2d1', mb: 2 }}>
                      {section.content}
                    </Typography>
                  )}

                  {section.items && Array.isArray(section.items) && (
                    <Box component="ul" sx={{ color: '#b8c2d1', pl: 2 }}>
                      {section.items.map((item, i) => (
                        <Box component="li" key={i} sx={{ mb: 1.5 }}>
                          {typeof item === 'string' ? (
                            <Typography variant="body1">{item}</Typography>
                          ) : (
                            <Box sx={{ p: 3, bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid #1f2a3d' }}>
                              <Typography variant="h6" sx={{ color: '#fff', mb: 1 }}>
                                {item.name}
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#b8c2d1' }}>
                                {item.description}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}

                  {section.tech && Array.isArray(section.tech) && (
                    <Stack spacing={2}>
                      {section.tech.map((t_item, i) => (
                        <Box key={i}>
                          <Typography variant="body1" sx={{ color: '#ef4444', fontWeight: 600, display: 'inline' }}>
                            {t_item.name} :
                          </Typography>
                          <Typography variant="body1" sx={{ color: '#b8c2d1', display: 'inline', ml: 1 }}>
                            {t_item.details}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  )}

                  {section.features && Array.isArray(section.features) && (
                    <Grid container spacing={3}>
                      {section.features.map((f, i) => (
                        <Grid item xs={12} key={i}>
                          <Box sx={{ p: 3, bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid #1f2a3d' }}>
                            <Typography variant="h6" sx={{ color: '#fff', mb: 1 }}>
                              {f.name}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#b8c2d1' }}>
                              {f.description}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Box>
              ))}

              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                <Button
                  href="https://haikei.art/"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained"
                  size="large"
                  endIcon={<Launch />}
                  sx={{ 
                    bgcolor: '#ef4444', 
                    '&:hover': { bgcolor: '#dc2626' },
                    color: '#fff',
                    px: 6,
                    py: 2,
                    fontSize: '1.1rem'
                  }}
                >
                  {t('haikei_page.visitSite')}
                </Button>
              </Box>
            </Box>
          </Grid>

          {/* Sidebar / screenshots */}
          <Grid item xs={12} md={4}>
            <Stack spacing={3}>
              <Typography variant="h5" sx={{ color: '#fff', fontWeight: 600 }}>
                {t('haikei_page.screenshots_title')}
              </Typography>
              {screenshots.map((s, i) => (
                <Box key={i}>
                  <Box
                    component="img"
                    src={s.src}
                    alt={s.title}
                    sx={{
                      width: '100%',
                      height: 'auto',
                      border: '1px solid #1f2a3d',
                      transition: 'transform 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.02)',
                        cursor: 'zoom-in'
                      }
                    }}
                    onClick={() => {
                      setSelectedImageIndex(i);
                      setCarouselOpen(true);
                    }}
                  />
                  <Typography variant="caption" sx={{ color: '#b8c2d1', mt: 1, display: 'block', textAlign: 'center' }}>
                    {s.title}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Grid>
        </Grid>
      </Container>

      {/* Image Carousel */}
      <ImageCarousel
        images={screenshots}
        open={carouselOpen}
        onClose={() => setCarouselOpen(false)}
        initialIndex={selectedImageIndex}
      />
    </Box>
    </>
  );
}

export default HaikeiArt;


